import { FC } from 'react';
import {
  AutocompleteControl,
  AutocompleteControlProps,
  ControlWithFormField,
  FormFieldControlProps
} from '@app/form_components';
import { useCurrentModule, useDesign } from '@app/hooks';
import {
  FormControlWithOptionProps,
  FormInputProps,
  ValueOrVariableType,
  VariableConfigField,
  VariableReference,
  VariableReferenceScope,
  VariableType
} from '@app/types';
import { getVariables, VariableOption } from '@app/utilities';

export type VariableAutocompleteControlProps = (
  FormControlWithOptionProps<
    VariableConfigField,
    VariableOption | null
  >
  & FormInputProps
  & Pick<AutocompleteControlProps<VariableReference, VariableOption>, 'labels'>
  & {
    /** Scope(s) to include in the variable suggestions. */
    searchScopes?: VariableReferenceScope[];
    /** Type(s) to include in the variable suggestions. */
    searchTypes?: VariableType[];
  }
);

export function VariableAutocompleteControl({
  labels,
  onChange,
  searchScopes,
  searchTypes,
  value,
  ...autocompleteProps
}: VariableAutocompleteControlProps): ReturnType<FC> {
  const serialized = useDesign();
  const serializedModule = useCurrentModule();

  const getOptions = (searchValue: string): VariableOption[] => {
    searchValue = searchValue.toLowerCase();

    return getVariables({
      serialized,
      serializedModule,
      searchScopes,
      searchTypes
    }).filter(({ label }) => label.toLowerCase().includes(searchValue));
  };

  return (
    <AutocompleteControl<VariableReference | '', VariableOption>
      {...autocompleteProps}
      getOptions={getOptions}
      initSearchValue={value?.varName ?? ''}
      labels={{
        isLoading: 'Searching variables...',
        notFound: '',
        ...labels
      }}
      onChange={(varName, option) => {
        onChange(
          {
            type: ValueOrVariableType.variable,
            varName: varName ?? ''
          },
          option
        );
      }}
      style={{ dropdown: { width: 'unset' } }}
      value={value?.varName ?? ''}
    />
  );
};

export type VariableAutocompleteProps = (
  VariableAutocompleteControlProps & FormFieldControlProps
);

export function VariableAutocomplete(
  props: VariableAutocompleteProps
): ReturnType<FC> {
  return (
    <ControlWithFormField control={VariableAutocompleteControl} {...props} />
  );
};
